import { Button, Grid, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { MultiInput } from "components/FormControls/MultiInput/MultiInput";
import { MultiWithModal } from "components/FormControls/MultiWithModal";
import { OLMenuButton } from "components/FormControls/OLMenuButton";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { ReactComponent as XCloseIcon } from "components/Icons/x-close.svg";
import { OLModal } from "components/Tooltips/OLModal";
import { Tooltip } from "components/Tooltips/Tooltip";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { kebabCase } from "lodash";
import { memo, useEffect, useState } from "react";
import { KeyValuePair } from "utilities/objects";
import { Dialog } from "./EffectDialog";

export const useStyles = makeStyles((theme: OLTheme) => ({
  modalDescription: {
    color: theme.gray500,
    marginTop: theme.spacing(-10),
    fontSize: "14px",
  },
  inputDialog: {
    width: "100%",
  },
  btnWrapper: {
    display: "flex",
    gap: theme.spacing(12),
    justifyContent: "space-between",
    marginTop: theme.spacing(12),

    "& button": {
      width: "100%",
      height: "100%",
    },
  },
  submitWrapper: {
    width: "100%",
  },
  textOperator: {
    fontSize: "16px",
    lineHeight: "24px",
    color: theme.gray500,
    margin: theme.spacing(12, 0),
  },
  deleteWrapper: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    margin: "auto",
    borderRadius: "50%",
    backgroundColor: "F0F0F0",
    width: "24px",
    minWidth: "24px",
    height: "24px",
    cursor: "pointer",
    "& svg": {
      color: theme.gray500,
    },
  },
}));

type Criteria =
  | "productName"
  | "productBrand"
  | "productCustomAttribute"
  | "productCategory";

type InitialValues = {
  productBrand: string[];
  productName: string[];
  productCategory: string[];
  productCustomAttribute: KeyValuePair[];
};

interface Props {
  isDialogOpen: boolean;
  dialog: Dialog | null;
  closeDialog: () => void;
  setFieldValue: (value: string) => void;
  currentValue: string;
  initialValues?: InitialValues;
}

const criteria = [
  { label: "productBrand", id: "productBrand" },
  { label: "productCategory", id: "productCategory" },
  { label: "productCustomAttribute", id: "productCustomAttribute" },
  { label: "productName", id: "productName" },
];

export const mapInitialValuesToSeleceted = (initialValues: InitialValues) => {
  const selected = [];

  for (const property in initialValues) {
    // @ts-ignore
    const isAnyItemAdded = initialValues[property].length > 0;

    if (isAnyItemAdded) {
      selected.push(property);
    }
  }

  return selected;
};

export const EffectItemsCriteriaDialog = memo(
  ({
    isDialogOpen,
    dialog,
    closeDialog,
    setFieldValue,
    currentValue,
    initialValues,
  }: Props) => {
    const [selectedCriteria, setCriteria] = useState<string[]>(
      initialValues ? mapInitialValuesToSeleceted(initialValues) : [],
    );

    useEffect(() => {
      if (initialValues) {
        setCriteria(mapInitialValuesToSeleceted(initialValues));
      }
    }, [initialValues]);
    const { message } = useMessage();
    const classes = useStyles();

    return isDialogOpen && dialog ? (
      <OLModal
        header={dialog.title}
        onClose={() => {
          setCriteria([]);
          closeDialog();
        }}
        open={isDialogOpen}
        modalWidth={700}
      >
        <Formik
          initialValues={
            initialValues || {
              productBrand: [],
              productName: [],
              productCategory: [],
              productCustomAttribute: [],
            }
          }
          onSubmit={async (values) => {
            const formatted = `(${JSON.stringify(
              values.productBrand || [],
            )}, ${JSON.stringify(values.productName || [])}, ${JSON.stringify(
              values.productCategory || [],
            )}, ${JSON.stringify(values.productCustomAttribute || [])})`;

            const injectedValues = dialog.code.replace("('')", formatted);

            setFieldValue(`${currentValue} <#>${injectedValues}</#>`);
            setCriteria([]);
            closeDialog();
          }}
        >
          {({ handleSubmit, isSubmitting, setFieldValue, values }) => {
            const areAllCriteriaSelected =
              selectedCriteria.length >= criteria.length;

            const isAnyCriteriaSelected = selectedCriteria.length > 0;
            const isAnyItemInCriteria =
              values.productBrand?.length > 0 ||
              values.productCategory?.length > 0 ||
              values.productCustomAttribute?.length > 0 ||
              values.productName?.length > 0;

            const unSelectCriteria = (criteria: Criteria) => {
              const indexToRemove = selectedCriteria.findIndex(
                (item) => item === criteria,
              );
              const criteriaCopy = [...selectedCriteria];
              criteriaCopy.splice(indexToRemove, 1);

              setCriteria(criteriaCopy);
              setFieldValue(criteria, []);
            };

            const isSubmitDisabled =
              isSubmitting || !isAnyCriteriaSelected || !isAnyItemInCriteria;

            return (
              <Form onSubmit={handleSubmit}>
                <div className={classes.modalDescription}>
                  {dialog.description}
                </div>

                <Typography className={classes.textOperator}>
                  {message({ id: "where" }).toUpperCase()}
                </Typography>
                {isAnyCriteriaSelected && (
                  <Grid container mb={12}>
                    {selectedCriteria.map((criteria, index) => {
                      const isNextCriteria = !!selectedCriteria[index + 1];

                      switch (criteria) {
                        case "productBrand":
                        case "productName":
                        case "productCategory":
                          return (
                            <>
                              <Grid item xs={11} key={criteria}>
                                <MultiInput
                                  label={
                                    <RequiredFieldLabel
                                      text={message({
                                        id: criteria
                                          .replace("product", "")
                                          .toLowerCase(),
                                      })}
                                    />
                                  }
                                  formField={criteria}
                                  placeholder={message({
                                    id: "multiInputHelper",
                                  })}
                                  data-test-id={kebabCase(criteria)}
                                />
                              </Grid>
                              <Grid
                                item
                                xs={1}
                                className={classes.deleteWrapper}
                                onClick={() => {
                                  unSelectCriteria(criteria);
                                }}
                              >
                                <XCloseIcon />
                              </Grid>
                              {isNextCriteria && (
                                <Grid item xs={12}>
                                  <Typography className={classes.textOperator}>
                                    {message({ id: "and" }).toUpperCase()}
                                  </Typography>
                                </Grid>
                              )}
                            </>
                          );
                        case "productCustomAttribute":
                          return (
                            <>
                              <Grid item xs={11} key={criteria}>
                                <MultiWithModal
                                  maxWidth="unset"
                                  data-test-id="custom-attributes"
                                  formField="productCustomAttribute"
                                  label={
                                    <RequiredFieldLabel
                                      text={message({
                                        id: "customAttribute",
                                      })}
                                    />
                                  }
                                  placeholder={message({
                                    id: "addCustomAttribute",
                                  })}
                                />
                              </Grid>
                              <Grid
                                item
                                xs={1}
                                className={classes.deleteWrapper}
                                onClick={() => unSelectCriteria(criteria)}
                              >
                                <XCloseIcon />
                              </Grid>
                              {isNextCriteria && (
                                <Grid item xs={12}>
                                  <Typography className={classes.textOperator}>
                                    {message({ id: "and" }).toUpperCase()}
                                  </Typography>
                                </Grid>
                              )}
                            </>
                          );
                        default:
                          return null;
                      }
                    })}
                  </Grid>
                )}

                {!areAllCriteriaSelected && (
                  <OLMenuButton
                    buttonVariant="outlined"
                    size="small"
                    startIcon={<PlusIcon />}
                    data-test-id="add-criteria"
                    items={criteria
                      .filter((item) => !selectedCriteria.includes(item.id))
                      .map((item) => ({
                        label: message({ id: item.label }),
                        onClick: () => {
                          setFieldValue(item.id, []);
                          const criteria = [...selectedCriteria, item.label];
                          setCriteria(criteria);
                        },
                      }))}
                    submit={message({
                      id: "addCriteria",
                    })}
                  />
                )}

                <div className={classes.btnWrapper}>
                  <Button variant="outlined" onClick={closeDialog}>
                    {message({ id: "cancel" })}
                  </Button>
                  <Tooltip
                    text={
                      isSubmitDisabled
                        ? message({ id: "addAtLeastOneCriterion" })
                        : ""
                    }
                  >
                    <div className={classes.submitWrapper}>
                      <Button
                        color="primary"
                        disabled={isSubmitDisabled}
                        fullWidth
                        type="submit"
                        variant="contained"
                        data-test-id="dialog-save"
                      >
                        {message({ id: "save" })}
                      </Button>
                    </div>
                  </Tooltip>
                </div>
              </Form>
            );
          }}
        </Formik>
      </OLModal>
    ) : null;
  },
);
